/**
 * StagioneSelector — dropdown delle stagioni disponibili.
 *
 * Usa /admin/stagioni (autenticato, no admin required).
 * Auto-seleziona la stagione attiva al primo render se nessun value.
 */

import { useEffect } from "react";
import { useStagioni } from "../../hooks/useStagione";
import { etichetta } from "../common/etichette";
import { tr } from "../../i18n";

interface StagioneSelectorProps {
  value: string;
  onChange: (id: string) => void;
  /** Se true, auto-seleziona la stagione attiva quando lista carica. Default true */
  autoSelectActive?: boolean;
  /** Label sopra il select */
  label?: string;
  className?: string;
}

export function StagioneSelector({
  value,
  onChange,
  autoSelectActive = true,
  label = tr("Stagione"),
  className = "",
}: StagioneSelectorProps) {
  const { data: stagioni, isLoading } = useStagioni();

  // Auto-select prima stagione attiva al primo load (se value vuoto)
  useEffect(() => {
    if (!autoSelectActive || value || !stagioni?.length) return;
    const attiva =
      stagioni.find((s) => s.stato === "in_corso") ??
      stagioni.find((s) =>
        ["mercato_estivo", "mercato_invernale", "lista_provvisoria"].includes(s.stato),
      ) ??
      stagioni[0];
    if (attiva) onChange(attiva.id);
  }, [stagioni, value, autoSelectActive, onChange]);

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">{label}</label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading || !stagioni?.length}
        className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
      >
        {isLoading && <option>{tr("Caricamento…")}</option>}
        {!isLoading && !stagioni?.length && <option value="">{tr("— nessuna stagione —")}</option>}
        {!isLoading && stagioni?.length && (
          <>
            <option value="">{tr("— seleziona —")}</option>
            {stagioni.map((s) => (
              <option key={s.id} value={s.id}>
                {s.anno} · {etichetta(s.stato)}
              </option>
            ))}
          </>
        )}
      </select>
    </div>
  );
}
